// The due-date history and the overdue pop-up, on the app's side (Carla, 2026-10-21). The server is
// the truth: Aidey Backend/lib/taskDueChanges.js logs every move, lib/overdueTasks.js decides which
// tasks are overdue (the one overdue rule) and what a move writes. This file only asks and shows.
import { api } from '../api/client';
import { formatTaskDay } from './taskTime';

// The one-tap reasons, in the order they are offered. Keys match the server's (and the table's CHECK).
export const MOVE_REASONS = [
  { value: 'waiting_on_client', label: 'Waiting on client' },
  { value: 'other_priority', label: 'Other work took priority' },
  { value: 'took_longer', label: 'Took longer than expected' },
  { value: 'no_longer_needed', label: 'No longer needed' },
  { value: 'other', label: 'Other' },
];
export const REASON_LABEL = Object.fromEntries(MOVE_REASONS.map((r) => [r.value, r.label]));
export const NOTE_MAX = 200;

// The task's id as a stored row: an occurrence drawn on screen names its series.
export const seriesIdOf = (task) => String(task?.parentTaskId || task?.id || '').split('-')[0];

// ── "Shown today" (a convenience, not the truth) ────────────────────────────────────────────────
// The pop-up opens by itself once per person per day. Which day it last opened is kept in this
// browser's localStorage, per user: it decides only whether to OPEN the pop-up, never what is in
// it (the server answers that). A private window or cleared storage just shows it again.
const shownKey = (userId) => `aidey_overdue_popup_shown:${userId}`;
export function shownToday(userId, today) {
  try { return localStorage.getItem(shownKey(userId)) === today; } catch (_) { return false; }
}
export function markShownToday(userId, today) {
  try { localStorage.setItem(shownKey(userId), today); } catch (_) { /* not remembered: shown again next time */ }
}

// ── Days ─────────────────────────────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
export const dayString = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export function addDays(dayStr, n) {
  const [y, m, d] = String(dayStr).split('-').map(Number);
  return dayString(new Date(y, m - 1, d + n));
}
// Next week: the Monday after today.
export function nextMonday(dayStr) {
  const [y, m, d] = String(dayStr).split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const add = ((8 - date.getDay()) % 7) || 7;
  return addDays(dayStr, add);
}

/** "2 Oct" for a stored due value. */
export const dueDay = (value) => formatTaskDay(value);

/** "Moved 3 times. Originally due 2 Oct" (or "Never moved."). */
export function historySummary({ moves, originalDue }) {
  if (!moves) return 'Never moved.';
  const times = moves === 1 ? 'once' : `${moves} times`;
  return `Moved ${times}.${originalDue ? ` Originally due ${dueDay(originalDue)}` : ''}`;
}

// ── The server ───────────────────────────────────────────────────────────────────────────────────
export async function fetchOverdue() {
  const res = await api('/api/tasks/overdue');
  return res.json();
}

// moves: [{ taskId, occurrenceStart?, to, reason?, note? }]. Resolves the server's answer, whatever
// it said for each; throws only when nothing came back.
export async function rescheduleTasks({ moves, source = 'popup', reason, note }) {
  try {
    const res = await api('/api/tasks/reschedule', { method: 'POST', body: JSON.stringify({ moves, source, reason, note }) });
    return res.json();
  } catch (err) {
    // A 400 still carries each move's own answer (a reason needed, a booking): pass it on.
    if (err && err.body && Array.isArray(err.body.results)) return err.body;
    throw err;
  }
}

export async function fetchDueHistory(taskId, occurrence) {
  const q = occurrence ? `?occurrence=${encodeURIComponent(occurrence)}` : '';
  const res = await api(`/api/tasks/${encodeURIComponent(taskId)}/due-history${q}`);
  return res.json();
}

export async function fetchMoveCounts(ids) {
  const list = [...new Set((ids || []).map(String).filter((i) => /^\d+$/.test(i)))];
  if (!list.length) return {};
  const res = await api(`/api/tasks/move-counts?ids=${list.join(',')}`);
  const data = await res.json();
  return data?.counts || {};
}

export async function fetchMovingMost() {
  const res = await api('/api/tasks/moving-most');
  return res.json();
}

/** "Moved 3×" from three moves on; nothing below. */
export const movedTag = (moves) => (Number(moves) >= 3 ? `Moved ${Number(moves)}×` : null);
